/*
  Cart extras for the cart drawer and cart page (own code, Dawn architecture):
  - <nt-cart-extras>  wrapper of the progress bar / timer / gift card; hosts the free gift controller
  - <nt-cart-timer>   "cart reserved" countdown, persisted in sessionStorage
  - <nt-cart-upsell>  recommended products row (section rendering of sections/cart-upsell.liquid)
  - <nt-cart-discount> discount code field (code travels to checkout as ?discount=CODE)
  Relies on constants.js / pubsub.js / global.js (routes, PUB_SUB_EVENTS, publish, subscribe, fetchConfig).
*/
(function () {
  const TIMER_KEY = 'nt_cart_timer';
  const DISCOUNT_KEY = 'nt_cart_discount';
  const GIFT_DECLINED_KEY = 'nt_gift_declined';
  const GIFT_PROP = '_nt_gift';

  const store = {
    get(key) {
      try {
        const v = window.sessionStorage.getItem(key);
        if (v !== null) return v;
      } catch (e) {}
      const m = document.cookie.match(new RegExp('(?:^|; )' + key + '=([^;]*)'));
      return m ? decodeURIComponent(m[1]) : null;
    },
    set(key, value) {
      try {
        window.sessionStorage.setItem(key, value);
      } catch (e) {}
      try {
        document.cookie = key + '=' + encodeURIComponent(value) + '; path=/; SameSite=Lax';
      } catch (e) {}
    },
    remove(key) {
      try {
        window.sessionStorage.removeItem(key);
      } catch (e) {}
      try {
        document.cookie = key + '=; path=/; Max-Age=0; SameSite=Lax';
      } catch (e) {}
    },
  };

  const cartJsonUrl = () => `${window.routes ? routes.cart_url : '/cart'}.js`;
  const rootUrl = () => (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  const json = (url, init) => fetch(url, init).then((r) => r.json());
  const jsonConfig = () => (typeof fetchConfig === 'function' ? fetchConfig() : { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' } });

  function refreshBubble() {
    const bubble = document.getElementById('cart-icon-bubble');
    if (!bubble) return Promise.resolve();
    return fetch(`${window.location.pathname}?section_id=cart-icon-bubble`)
      .then((r) => r.text())
      .then((html) => {
        const el = new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section');
        if (el) bubble.innerHTML = el.innerHTML;
      })
      .catch(() => {});
  }

  /* ---------- free gift controller ---------- */
  const gift = {
    busy: false,
    again: false,
    timer: null,
    schedule() {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.evaluate(), 120);
    },
    config() {
      const el = document.querySelector('nt-cart-extras[data-gift-variant]');
      if (!el) return null;
      const amount = parseInt(el.dataset.giftAmount, 10);
      const variant = parseInt(el.dataset.giftVariant, 10);
      return amount > 0 && variant ? { amount, variant } : null;
    },
    async evaluate() {
      if (this.busy) {
        this.again = true;
        return;
      }
      const conf = this.config();
      if (!conf) return;
      this.busy = true;
      let changed = false;
      try {
        const cart = await json(cartJsonUrl());
        const giftLines = cart.items.filter((i) => i.properties && i.properties[GIFT_PROP] === 'true');
        const giftValue = giftLines.reduce((sum, i) => sum + i.final_line_price, 0);
        const qualifying = cart.items_subtotal_price - giftValue;
        const others = cart.items.length - giftLines.length;
        if (others > 0 && qualifying >= conf.amount) {
          if (!giftLines.length) {
            if (store.get(GIFT_DECLINED_KEY) !== '1') {
              const res = await fetch(`${routes.cart_add_url}.js`, {
                ...jsonConfig(),
                body: JSON.stringify({ items: [{ id: conf.variant, quantity: 1, properties: { [GIFT_PROP]: 'true' } }] }),
              });
              changed = res.ok;
            }
          } else {
            for (const line of giftLines) {
              if (line.quantity > 1) {
                await fetch(routes.cart_change_url, { ...jsonConfig(), body: JSON.stringify({ id: line.key, quantity: 1 }) });
                changed = true;
              }
            }
          }
        } else {
          store.remove(GIFT_DECLINED_KEY);
          for (const line of giftLines) {
            await fetch(routes.cart_change_url, { ...jsonConfig(), body: JSON.stringify({ id: line.key, quantity: 0 }) });
            changed = true;
          }
        }
      } catch (e) {
        /* leave the cart untouched on any error */
      }
      this.busy = false;
      if (changed) {
        await refreshBubble();
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'nt-gift' });
      } else if (this.again) {
        this.again = false;
        this.schedule();
      }
    },
  };

  // Customer removed the free gift on purpose: do not re-add it until the cart drops below the threshold.
  document.addEventListener(
    'click',
    (event) => {
      if (event.target.closest && event.target.closest('[data-nt-gift] cart-remove-button')) store.set(GIFT_DECLINED_KEY, '1');
    },
    true
  );

  if (typeof subscribe === 'function') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event && event.source === 'nt-gift') return;
      gift.schedule();
    });
  }

  if (!customElements.get('nt-cart-extras')) {
    customElements.define(
      'nt-cart-extras',
      class NtCartExtras extends HTMLElement {
        connectedCallback() {
          if (this.dataset.giftVariant) gift.schedule();
        }
      }
    );
  }

  /* ---------- timer ---------- */
  if (!customElements.get('nt-cart-timer')) {
    customElements.define(
      'nt-cart-timer',
      class NtCartTimer extends HTMLElement {
        connectedCallback() {
          if (this.hasAttribute('data-clear')) {
            store.remove(TIMER_KEY);
            return;
          }
          this.textEl = this.querySelector('.nt-timer__text');
          this.minutes = Math.max(1, parseInt(this.dataset.minutes, 10) || 10);
          this.mode = this.dataset.mode || 'reset';
          this.template = this.textEl ? this.textEl.dataset.ntTemplate || '[time]' : '[time]';
          let state = null;
          try {
            state = JSON.parse(store.get(TIMER_KEY));
          } catch (e) {}
          if (!state || typeof state.end !== 'number') {
            state = { end: Date.now() + this.minutes * 60000, done: false };
            store.set(TIMER_KEY, JSON.stringify(state));
          }
          this.state = state;
          this.tick();
          this.interval = setInterval(() => this.tick(), 1000);
        }

        disconnectedCallback() {
          clearInterval(this.interval);
        }

        format(ms) {
          const total = Math.max(0, Math.ceil(ms / 1000));
          const m = Math.floor(total / 60);
          const s = total % 60;
          return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
        }

        tick() {
          if (this.state.done) return this.showExpired();
          const left = this.state.end - Date.now();
          if (left > 0) {
            this.hidden = false;
            this.textEl.textContent = this.template.replace('[time]', this.format(left));
            return;
          }
          if (this.mode === 'reset') {
            this.state = { end: Date.now() + this.minutes * 60000, done: false };
          } else {
            this.state = { end: this.state.end, done: true };
          }
          store.set(TIMER_KEY, JSON.stringify(this.state));
          this.tick();
        }

        showExpired() {
          clearInterval(this.interval);
          if (this.mode === 'message' && this.dataset.expiredText) {
            this.hidden = false;
            this.classList.add('is-expired');
            this.textEl.textContent = this.dataset.expiredText;
          } else {
            this.hidden = true;
          }
        }
      }
    );
  }

  /* ---------- upsell ---------- */
  const upsellCache = {};
  if (!customElements.get('nt-cart-upsell')) {
    customElements.define(
      'nt-cart-upsell',
      class NtCartUpsell extends HTMLElement {
        connectedCallback() {
          this.url = this.dataset.url;
          if (upsellCache[this.url]) this.paint(upsellCache[this.url]);
          this.load();
          this.addEventListener('click', this.onClick);
        }

        disconnectedCallback() {
          this.removeEventListener('click', this.onClick);
        }

        load() {
          fetch(this.url)
            .then((r) => r.text())
            .then((html) => {
              const doc = new DOMParser().parseFromString(html, 'text/html');
              const block = doc.querySelector('.nt-upsell');
              upsellCache[this.url] = block ? block.outerHTML : '';
              this.paint(upsellCache[this.url]);
            })
            .catch(() => {});
        }

        paint(markup) {
          if (this.dataset.painted === markup) return;
          this.dataset.painted = markup;
          this.innerHTML = markup;
        }

        onClick = (event) => {
          const button = event.target.closest('[data-nt-upsell-add]');
          if (!button || button.disabled) return;
          const item = button.closest('.nt-upsell__item');
          const select = item && item.querySelector('.nt-upsell__select');
          const id = parseInt(select ? select.value : button.dataset.variantId, 10);
          if (!id) return;
          this.add(id, button);
        };

        add(id, button) {
          const drawer = document.querySelector('cart-drawer');
          const inDrawer = this.dataset.context === 'drawer' && drawer;
          const status = this.querySelector('.nt-upsell__status');
          button.disabled = true;
          button.classList.add('is-loading');
          const body = { items: [{ id, quantity: 1 }] };
          if (inDrawer) {
            body.sections = 'cart-drawer,cart-icon-bubble';
            body.sections_url = window.location.pathname;
          }
          fetch(`${routes.cart_add_url}.js`, { ...jsonConfig(), body: JSON.stringify(body) })
            .then((r) => r.json())
            .then((state) => {
              if (state.status || state.errors) throw new Error(state.description || state.message || 'error');
              if (inDrawer && state.sections) {
                drawer.renderContents(state);
                gift.schedule();
              } else {
                refreshBubble();
                publish(PUB_SUB_EVENTS.cartUpdate, { source: 'nt-upsell', cartData: state });
              }
            })
            .catch(() => {
              button.disabled = false;
              button.classList.remove('is-loading');
              if (status) status.textContent = window.cartStrings ? cartStrings.error : '';
            });
        }
      }
    );
  }

  /* ---------- discount code ---------- */
  if (!customElements.get('nt-cart-discount')) {
    customElements.define(
      'nt-cart-discount',
      class NtCartDiscount extends HTMLElement {
        connectedCallback() {
          this.form = this.querySelector('.nt-discount__form');
          this.input = this.querySelector('.nt-discount__input');
          this.chip = this.querySelector('.nt-discount__chip');
          this.chipText = this.querySelector('.nt-discount__chip-text');
          this.form.addEventListener('submit', (e) => {
            e.preventDefault();
            this.apply(this.input.value);
          });
          this.querySelector('.nt-discount__clear').addEventListener('click', () => this.clear());
          const saved = store.get(DISCOUNT_KEY);
          this.render(saved, saved ? store.get(DISCOUNT_KEY + '_state') || 'pending' : null);
        }

        render(code, state) {
          const has = Boolean(code);
          this.form.hidden = has;
          this.chip.hidden = !has;
          this.chip.classList.toggle('is-applied', state === 'applied');
          if (has) {
            const tpl = state === 'applied' ? this.dataset.appliedText : this.dataset.pendingText;
            this.chipText.textContent = tpl.replace('[code]', code);
          } else {
            this.input.value = '';
          }
        }

        async apply(raw) {
          const code = (raw || '').trim().replace(/\s+/g, '').toUpperCase();
          if (!code) {
            this.input.focus();
            return;
          }
          store.set(DISCOUNT_KEY, code);
          store.set(DISCOUNT_KEY + '_state', 'pending');
          let state = 'pending';
          if (this.dataset.preview === 'true') {
            try {
              // Visiting /discount/CODE stores the code for this browser; the cart then shows it when it is valid.
              await fetch(`${rootUrl()}discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(cartJsonUrl())}`);
              const cart = await json(cartJsonUrl());
              const titles = [];
              (cart.cart_level_discount_applications || []).forEach((d) => titles.push(d.title));
              cart.items.forEach((i) => (i.discounts || []).forEach((d) => titles.push(d.title)));
              if (titles.some((t) => String(t).toUpperCase() === code)) state = 'applied';
              store.set(DISCOUNT_KEY + '_state', state);
              publish(PUB_SUB_EVENTS.cartUpdate, { source: 'nt-discount' });
            } catch (e) {}
          }
          this.render(code, state);
        }

        clear() {
          store.remove(DISCOUNT_KEY);
          store.remove(DISCOUNT_KEY + '_state');
          try {
            document.cookie = 'discount_code=; path=/; Max-Age=0';
          } catch (e) {}
          this.render(null, null);
          this.input.focus();
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'nt-discount' });
        }
      }
    );
  }

  // Send the code along to checkout: /checkout?discount=CODE (Shopify has no AJAX endpoint to apply codes).
  document.addEventListener(
    'click',
    (event) => {
      const button = event.target.closest && event.target.closest('[name="checkout"]');
      if (!button || button.disabled || event.defaultPrevented) return;
      const code = store.get(DISCOUNT_KEY);
      if (!code) return;
      event.preventDefault();
      window.location.href = `${rootUrl()}checkout?discount=${encodeURIComponent(code)}`;
    },
    false
  );

  // Check the free gift once the page is ready.
  document.addEventListener('DOMContentLoaded', () => gift.schedule());
})();
